import { RmaCookieOptions, RmaCookieStorage } from './cookie.model';

export function checkIfCookieExists(cookieName: string, cookieJar: string) {
  const regExp = getCookieRegularExpression(cookieName);
  return regExp.test(cookieJar);
}

export function getCookieByName(cookieName: string, cookieJar: string): string {
  const regExp = getCookieRegularExpression(cookieName);
  const result = regExp.exec(cookieJar);
  const value = result?.[1];
  return value ? safeDecodeURIComponent(value) : '';
}

export function buildCookieString(cookieName: string, value: string, options: RmaCookieOptions): string {
  let cookieString = `${encodeURIComponent(cookieName)}=${encodeURIComponent(value)};`;

  cookieString += appendExpiresDirective(options.expires);
  cookieString += appendPathDirective(options.path);
  cookieString += appendDomainDirective(options.domain);
  cookieString += appendSecurityDirectives(cookieName, options);

  return cookieString;
}

export function convertCookiesToObject(cookieJar?: string | null) {
  const cookies: RmaCookieStorage = {};
  if (!cookieJar || cookieJar === '') {
    return cookies;
  }

  return cookieJar
    .split(';')
    .filter(Boolean)
    .reduce((accumulated: RmaCookieStorage, currentCookie: string) => {
      const [cookieName, cookieValue] = currentCookie.split('=');
      if (!cookieName) {
        return accumulated;
      }
      const key = safeDecodeURIComponent(cookieName.replace(/^ /, ''));
      const value = safeDecodeURIComponent(cookieValue ?? '').trim();
      return { ...accumulated, [key]: value };
    }, cookies);
}

function getCookieRegularExpression(cookieName: string): RegExp {
  const encodedName = encodeURIComponent(cookieName);
  const escapedName: string = encodedName.replace(/([\\[\]{}()|=;+?,.*^$])/gi, '\\$1');
  return new RegExp(`(?:^${escapedName}|;\\s*${escapedName})=(.*?)(?:;|$)`, 'g');
}

function appendSecurityDirectives(cookieName: string, options: RmaCookieOptions): string {
  let securityString = '';

  if (options.secure === false && options.sameSite === 'None') {
    options.secure = true;
    console.warn(
      `[rma-cookie-service] Cookie ${cookieName} was forced with secure flag because sameSite=None.` +
        `More details : https://github.com/stevermeister/ngx-cookie-service/issues/86#issuecomment-597720130`,
    );
  }
  if (options.secure) {
    securityString += 'secure;';
  }

  if (!options.sameSite) {
    options.sameSite = 'Lax';
  }

  securityString += `sameSite=${options.sameSite};`;

  if (options.partitioned) {
    securityString += 'Partitioned;';
  }

  return securityString;
}

function appendExpiresDirective(expires?: number | Date): string {
  if (!expires) return '';

  let expiresString = 'expires=';
  if (typeof expires === 'number') {
    const dateExpires: Date = new Date(new Date().getTime() + expires * 1000 * 60 * 60 * 24);
    expiresString += dateExpires.toUTCString();
  } else {
    expiresString += expires.toUTCString();
  }

  return `${expiresString};`;
}

function appendPathDirective(path?: string): string {
  return path ? `path=${path};` : '';
}

function appendDomainDirective(domain?: string): string {
  return domain ? `domain=${domain};` : '';
}

function safeDecodeURIComponent(encodedURIComponent: string): string {
  try {
    return decodeURIComponent(encodedURIComponent);
  } catch {
    return encodedURIComponent;
  }
}
